import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  forwardRef,
  Input,
  input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { RadioFormGroupItemComponent } from './radio-form-group-item/radio-form-group-item.component';
import type { IconName } from '../../common/icon/icons.type';
import { KeyCode } from '../../core/keycode';

export const RADIO_GROUP_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => RadioFormGroupComponent),
  multi: true,
};

export interface RadioGroupItem<T = any> {
  option: string;
  value: T;
  description?: string;
  template?: TemplateRef<{ $implicit: RadioGroupItem; currentValue: T }>;
}

@Component({
  selector: 'rt-radio-form-group',
  templateUrl: 'radio-form-group.component.html',
  styleUrls: ['./radio-form-group.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RADIO_GROUP_VALUE_ACCESSOR],
  host: {
    '[class.disabled]': 'disabled',
    '[class.horizontal]': 'horizontal()',
  },
  imports: [NgTemplateOutlet, RadioFormGroupItemComponent],
})
export class RadioFormGroupComponent implements ControlValueAccessor {
  @Input() public items: RadioGroupItem[] = [];
  @Input() public icon: IconName;
  @Input() public disabled: boolean = false;

  @ViewChild('optionDefaultTemplate', { static: true })
  public optionDefaultTemplate: TemplateRef<any>;
  @ContentChild('optionCustomTemplate', { static: true })
  public optionCustomTemplate: TemplateRef<any>;

  public horizontal = input(false);

  public get itemTemplate(): TemplateRef<any> {
    return this.optionCustomTemplate || this.optionDefaultTemplate;
  }

  private _value: string;

  public get value(): string {
    return this._value;
  }

  public set value(value: string) {
    this._value = value;
    this.onChange(this._value);
  }

  constructor(private cdRef: ChangeDetectorRef) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};

  public isChecked(item: RadioGroupItem): boolean {
    return item.value === this.value;
  }

  public onClick(item: RadioGroupItem): void {
    this.value = item.value;
    this.cdRef.detectChanges();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    const key = event.which || event.keyCode;
    // eslint-disable-next-line default-case
    switch (key) {
      case KeyCode.UpArrow:
        event.preventDefault();
        this.selectUp();
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        this.selectDown();
        break;
    }
  }

  public trackByItem(index: number, item: { value?: string | number }): string | number {
    return item.value || index;
  }

  public writeValue(value: string): void {
    this._value = value;
    this.cdRef.detectChanges();
  }

  public registerOnChange(fn: (_: string) => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange = (_: string) => {};

  private selectUp(): void {
    const checkedOption = this.items.findIndex(item => item.value === this.value);

    if (checkedOption === -1) {
      this.value = this.items[0].value;
      return;
    }

    if (checkedOption === 0 || checkedOption > this.items.length - 1) {
      this.value = this.items[this.items.length - 1].value;
    } else {
      this.value = this.items[checkedOption - 1].value;
    }
  }

  private selectDown(): void {
    const checkedOption = this.items.findIndex(item => item.value === this.value);

    if (checkedOption === -1) {
      this.value = this.items[0].value;
      return;
    }

    if (checkedOption >= this.items.length - 1) {
      this.value = this.items[0].value;
    } else {
      this.value = this.items[checkedOption + 1].value;
    }
  }
}
